import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAbility } from '../../stores/authStore';
import type { Action, Subject } from '../../types/ability';

interface ProtectedRouteProps {
  children: ReactNode;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Optional ability gate. When provided, the route requires the user's ability
   * to allow `action` on `subject`. Failed checks redirect to `/` (not a 403
   * page) to avoid accidental lockouts during initial rollout.
   */
  can?: [Action, Subject];
}

export function ProtectedRoute({ children, isAuthenticated, isLoading, can }: ProtectedRouteProps) {
  const { t } = useTranslation('shared');
  const ability = useAbility();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-lg text-muted-foreground">{t('auth.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (can && !(ability?.can(...can) ?? false)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
